import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Typography } from '@mui/material';
import { apiErrorMessage } from '../api';
import { launchColors } from '../launchTheme';
import { dateTimeText, personName } from '../productInfo/productLabels';
import { AlcoholHistoryEntry } from './alcoholTypes';

interface Props {
  open: boolean;
  title: string;
  label: string;
  current: string;
  names: Record<string, string>;
  canRestore: boolean;
  load: () => Promise<AlcoholHistoryEntry[]>;
  onRestore: (text: string) => void;
  onClose: () => void;
}

export interface TextVersion { text: string; at: string; by: string }

export function textVersions(history: AlcoholHistoryEntry[], label: string): TextVersion[] {
  const changes = history.flatMap((entry) => entry.changes
    .filter((change) => change.label === label)
    .map((change) => ({ change, at: entry.at, by: entry.by })));
  const versions: TextVersion[] = changes.map(({ change, at, by }) => ({ text: change.after, at, by }));
  const oldest = changes[changes.length - 1];
  if (oldest) versions.push({ text: oldest.change.before, at: '', by: '' });
  return versions.filter((version) => version.text.trim());
}

const TextHistoryDialog: React.FC<Props> = ({ open, title, label, current, names, canRestore, load, onRestore, onClose }) => {
  const [versions, setVersions] = useState<TextVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setVersions(null);
    setError(null);
    load()
      .then((history) => { if (!cancelled) setVersions(textVersions(history, label)); })
      .catch((requestError) => { if (!cancelled) setError(apiErrorMessage(requestError, 'Historiken kunde inte hämtas.')); });
    return () => { cancelled = true; };
  }, [label, load, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!versions && !error && <LinearProgress />}
        {versions && versions.length === 0 && (
          <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Inga tidigare texter.</Typography>
        )}
        <Stack spacing={1.25} sx={{ mt: 0.5 }}>
          {versions?.map((version, index) => {
            const isCurrent = version.text === current;
            return (
              <Box key={`${version.at}-${index}`} sx={{ border: `1px solid ${launchColors.divider}`, borderRadius: '10px', p: 1.25, bgcolor: isCurrent ? launchColors.section : launchColors.card }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: launchColors.textSecondary, flex: 1 }}>
                    {version.at ? [personName(version.by, names), dateTimeText(version.at)].filter(Boolean).join(' · ') : 'Före första ändringen'}
                    {isCurrent ? ' · nuvarande text' : ''}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: launchColors.textMuted }}>{version.text.length} tecken</Typography>
                  {canRestore && !isCurrent && (
                    <Button size="small" onClick={() => onRestore(version.text)}>Ta tillbaka</Button>
                  )}
                </Stack>
                <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{version.text}</Typography>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TextHistoryDialog;
